import React, { useMemo, useState, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { doc, updateDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../context/AuthContext";
import "./BuyCredits.css";
import "./Checkout.css";

const clampInt = (v, min, max) => {
  const n = Math.floor(Number(v));
  if (!Number.isFinite(n)) return min;
  return Math.max(min, Math.min(max, n));
};

const estimateUsdForCredits = (credits) => {
  const c = Number(credits);
  if (!Number.isFinite(c) || c <= 0) return 0;

  const basePerCredit = 0.2; // $0.20 per credit
  let discount = 0;

  if (c >= 500) discount = 0.40;
  else if (c >= 250) discount = 0.30;
  else if (c >= 100) discount = 0.25;
  else if (c >= 50) discount = 0.20;
  else if (c >= 25) discount = 0.10;

  const raw = c * basePerCredit;
  const discounted = raw * (1 - discount);

  return Math.round(discounted * 100) / 100;
};

const BuyCredits = ({ onClose }) => {
  const navigate = useNavigate();
  const { currentUser, userData, userSettings, loading } = useAuth();

  const [creditsInput, setCreditsInput] = useState(25);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const presets = useMemo(() => [5, 25, 50, 100, 250, 500, 1000], []);

  // Display currency (UI only)
  const [displayCurrency, setDisplayCurrency] = useState("USD");
  const [fxRate, setFxRate] = useState(null); // USD -> displayCurrency
  const [fxFetchedAt, setFxFetchedAt] = useState("");

  // Env: your deployed function URLs
  const PAYSTACK_INIT_URL = process.env.REACT_APP_PAYSTACK_INIT_URL; // createPaystackCheckout
  const FX_RATE_URL = process.env.REACT_APP_FX_RATE_URL; // getFxRate

  const settingsRef = useMemo(() => {
    if (!currentUser) return null;
    return doc(db, "users", currentUser.uid, "settings", "main");
  }, [currentUser]);

  const formatMoney = (amount, currency) => {
    try {
      return new Intl.NumberFormat(undefined, { style: "currency", currency }).format(amount);
    } catch {
      return `${currency} ${amount}`;
    }
  };

  // Best-effort default display currency from locale
  useEffect(() => {
    try {
      const locale = navigator.language || "en-US";
      if (locale.includes("ZA")) setDisplayCurrency("ZAR");
      else if (locale.includes("GB")) setDisplayCurrency("GBP");
      else if (locale.startsWith("de") || locale.startsWith("fr") || locale.startsWith("es") || locale.startsWith("it"))
        setDisplayCurrency("EUR");
      else setDisplayCurrency("USD");
    } catch {
      setDisplayCurrency("USD");
    }
  }, []);

  // Live FX: USD -> displayCurrency (UI estimate)
  useEffect(() => {
    const run = async () => {
      if (!FX_RATE_URL) return;
      if (!displayCurrency) return;

      try {
        setFxRate(null);
        setFxFetchedAt("");

        if (displayCurrency === "USD") {
          setFxRate(1);
          setFxFetchedAt(new Date().toISOString());
          return;
        }

        const url = `${FX_RATE_URL}?from=USD&to=${encodeURIComponent(displayCurrency)}`;
        const resp = await fetch(url);
        const data = await resp.json();

        if (!resp.ok) throw new Error(data?.error || "FX fetch failed");

        setFxRate(Number(data.rate));
        setFxFetchedAt(data.fetchedAt || "");
      } catch {
        setFxRate(null);
        setFxFetchedAt("");
      }
    };

    run();
  }, [FX_RATE_URL, displayCurrency]);

  const credits = clampInt(creditsInput, 1, 100000);
  const tokens = credits * 1000;
  const estimatedUsd = estimateUsdForCredits(credits);
  const perCredit = credits > 0 ? Math.round((estimatedUsd / credits) * 1000) / 1000 : 0;

  const estimatedLocal = useMemo(() => {
    if (displayCurrency === "USD") return estimatedUsd;
    if (!fxRate) return null;
    return Number((estimatedUsd * fxRate).toFixed(2));
  }, [estimatedUsd, fxRate, displayCurrency]);

  const beginCreditCheckout = useCallback(async () => {
    if (loading) return;

    if (!currentUser) {
      navigate("/authentication", { replace: true });
      return;
    }

    if (!settingsRef) {
      setError("Could not load your billing settings. Please refresh.");
      return;
    }

    if (!PAYSTACK_INIT_URL) {
      setError("Missing REACT_APP_PAYSTACK_INIT_URL in your frontend environment.");
      return;
    }

    setBusy(true);
    setError("");

    try {
      // Save intent in Firestore (survives refresh)
      await updateDoc(settingsRef, {
        creditPurchaseStatus: "payment-ongoing",
        creditPurchaseCredits: credits,
        creditPurchaseTokens: tokens,
        creditPurchaseAmountUsdEstimated: estimatedUsd,
        creditPurchaseDisplayCurrency: displayCurrency,
        updatedAt: serverTimestamp(),
      });

      // Create Paystack session immediately (redirect directly to Paystack)
      const resp = await fetch(PAYSTACK_INIT_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          uid: currentUser.uid,
          email: currentUser.email || "",
          purchaseType: "credits",
          creditPackKey: null,          // you are using custom credits input
          creditsQty: credits,          // server may use this if you allow custom qty
          amountUsd: estimatedUsd,      // server SHOULD enforce pricing (recommended)
          displayCurrency,
        }),
      });

      const data = await resp.json();
      if (!resp.ok) throw new Error(data?.error || "Could not create Paystack checkout.");
      if (!data?.authorization_url) throw new Error("Paystack did not return an authorization_url.");

      // store reference for callback fallback
      if (data?.reference) {
        localStorage.setItem("kairos_last_paystack_reference", data.reference);
      }

      window.location.href = data.authorization_url;
    } catch (e) {
      setError(e?.message || "Could not start credits checkout. Try again.");
    } finally {
      setBusy(false);
    }
  }, [ credits, tokens, estimatedUsd, displayCurrency, currentUser, loading, navigate, settingsRef, PAYSTACK_INIT_URL]);

  const currentCredits = userData?.credits ?? 0;
  const planKey = (userSettings?.planKey || "free").toLowerCase();

  const goBack = () => {
    navigate("/workspace", { replace: true });
  };

  return (
    <main className="home">
      <section className="checkout buy-credits-wrapper">
        <div className="checkout-header">
          <h2 onClick={onClose} style={{ cursor: onClose ? "pointer" : "default" }}>
            Buy <span className="gradient-text">Credits</span>
          </h2>
          <p className="checkout-subtitle">
            Credits are used for AI actions. <b>1 credit = 1,000 tokens</b>. Larger bundles get better rates.
          </p>
        </div>

        <div className="credits-layout">
          {/* LEFT */}
          <div className="checkout-card credits-card">
            <div className="credits-top">
              <div>
                <h3>Your Credits</h3>
                <p className="tiny muted">
                  Current plan: <b className="cap">{planKey}</b>
                </p>
              </div>

              <div className="credits-balance">
                <span className="credits-balance-label">Balance</span>
                <span className="credits-balance-value">{currentCredits}</span>
              </div>
            </div>

            <div className="credits-input-wrap">
              <label className="credits-label" htmlFor="credits">
                Enter credits to buy
              </label>

              <div className="credits-input-row">
                <input
                  id="credits"
                  type="number"
                  min={1}
                  max={100000}
                  value={creditsInput}
                  onChange={(e) => setCreditsInput(e.target.value)}
                  disabled={busy}
                  className="credits-input"
                />
                <div className="credits-unit">credits</div>
              </div>

              <div className="credits-presets">
                {presets.map((c) => (
                  <button
                    key={c}
                    type="button"
                    className={`preset-btn ${credits === c ? "active" : ""}`}
                    onClick={() => setCreditsInput(c)}
                    disabled={busy}
                  >
                    {c}
                  </button>
                ))}
              </div>

              <div className="mini-details credits-mini">
                <div className="mini-row">
                  <span>Display currency</span>
                  <span className="muted">
                    <select
                      className="currency-select"
                      value={displayCurrency}
                      onChange={(e) => setDisplayCurrency(e.target.value)}
                      disabled={busy}
                    >
                      <option value="USD">USD</option>
                      <option value="ZAR">ZAR</option>
                      <option value="EUR">EUR</option>
                      <option value="GBP">GBP</option>
                      <option value="AUD">AUD</option>
                      <option value="CAD">CAD</option>
                    </select>
                  </span>
                </div>

                <div className="mini-row">
                  <span>Charged via Paystack</span>
                  <span className="muted">ZAR (USD→ZAR computed at checkout)</span>
                </div>

                <div className="mini-row">
                  <span>FX updated</span>
                  <span className="muted">{fxFetchedAt ? new Date(fxFetchedAt).toLocaleString() : "—"}</span>
                </div>
              </div>

              <p className="tiny muted" style={{ marginTop: 10 }}>
                Tip: 5 credits = about 1 small website build session (your estimate).
              </p>
            </div>

            {error && (
              <p className="error" style={{ marginTop: 10 }}>
                {error}
              </p>
            )}

            <div className="credits-actions">
              <button className="primary-btn" type="button" onClick={beginCreditCheckout} disabled={busy || credits <= 0}>
                {busy ? "Opening Paystack..." : "Proceed to payment"}
              </button>

              <button className="secondary-btn" type="button" onClick={goBack} disabled={busy}>
                Back
              </button>
            </div>
          </div>

          {/* RIGHT */}
          <div className="checkout-card featured credits-summary">
            <h3>Summary</h3>

            <div className="summary-box">
              <div className="summary-row">
                <span>Credits</span>
                <span className="summary-total">{credits}</span>
              </div>

              <div className="summary-row">
                <span>Tokens</span>
                <span className="muted">{tokens.toLocaleString()}</span>
              </div>

              <div className="summary-row">
                <span>Estimated total (USD)</span>
                <span className="summary-total">{formatMoney(estimatedUsd, "USD")}</span>
              </div>

              <div className="summary-row">
                <span>Approx ({displayCurrency})</span>
                <span className="summary-total">
                  {estimatedLocal == null ? "Loading rate..." : formatMoney(estimatedLocal, displayCurrency)}
                </span>
              </div>

              <div className="summary-row">
                <span>Est. rate</span>
                <span className="muted">{formatMoney(perCredit, "USD")} / credit</span>
              </div>

              <div className="summary-note">
                <p>
                  You are USD-priced. Paystack will charge in <b>ZAR</b> using a live <b>USD→ZAR</b> rate at checkout time.
                </p>
              </div>
            </div>

            <div className="credits-fineprint">
              <p className="tiny">
                Credits are once-off purchases (not recurring). Credits are added after payment verification.
              </p>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default BuyCredits;
